import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  AlertCircle,
  Users,
  FileText,
  Loader,
  Wallet,
  Battery,
  CreditCard,
} from "lucide-react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { realAPI } from "../../services/realAPI";
import {
  ProgressivePricingType,
  ProgressivePlanQuota,
  OrganizationWithQuotas,
  ProgressivePricingPlan,
} from "../../types/types";

interface QuotaManagementProps {
  onUpdate?: () => void;
}

const QuotaManagement: React.FC<QuotaManagementProps> = ({ onUpdate }) => {
  const [quotas, setQuotas] = useState<ProgressivePlanQuota[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toppingUp, setToppingUp] = useState<number | null>(null);
  const [topupSuccess, setTopupSuccess] = useState<string | null>(null);
  const [currentOrganization, setCurrentOrganization] =
    useState<OrganizationWithQuotas | null>(null);
  const [selectedTopupPlan, setSelectedTopupPlan] = useState<{
    [key: number]: number;
  }>({});
  const [availableTopupPlans, setAvailableTopupPlans] = useState<
    ProgressivePricingPlan[]
  >([]);
  const [showTopupModal, setShowTopupModal] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [orgResponse, plansResponse] = await Promise.all([
        realAPI.getCurrentOrganization(),
        realAPI.getAvailableProgressivePlans(),
      ]);

      console.log("Organization response:", orgResponse);
      console.log("Progressive plans response:", plansResponse);

      setCurrentOrganization(orgResponse);
      if (orgResponse?.progressivePlanQuotas) {
        setQuotas(orgResponse.progressivePlanQuotas);
      }

      if (plansResponse?.data?.plans) {
        // Filter only non-free (topup) plans
        const topupPlans = plansResponse.data.plans.filter(
          (plan: ProgressivePricingPlan) => plan.priceCents > 0 && plan.isActive
        );
        setAvailableTopupPlans(topupPlans);
      }
    } catch (err) {
      console.error("Error loading quota data:", err);
      setError("Failed to load quota information. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Debug PayPal configuration
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  console.log(
    "💳 PayPal SDK:",
    paypalClientId
      ? "Configured ✅"
      : "Missing client ID ⚠️ (set REACT_APP_PAYPAL_CLIENT_ID)"
  );

  // Client-side capture path removed; capture is handled on backend now.

  const handlePayPalError = (err: any) => {
    console.error("PayPal error:", err);
    setError("Payment failed. Please try again.");
    setToppingUp(null);
  };

  // Removed Test Payment helper to use only real PayPal flow

  // Build the payload for backend order creation
  const buildCreateOrderPayload = (quotaId: number) => {
    const selectedPlanId = selectedTopupPlan[quotaId];
    const selectedPlan = availableTopupPlans.find(
      (plan) => plan.id === selectedPlanId
    );
    if (!selectedPlan) throw new Error("No plan selected");

    const org = currentOrganization;
    const userStr = localStorage.getItem("currentUser");
    const user = userStr ? JSON.parse(userStr) : null;
    const topupId = `${Date.now()}_${quotaId}_${selectedPlan.id}`; // simple client-generated correlation ID
    const amountStr = (selectedPlan.priceCents / 100).toFixed(2);

    return {
      topupId,
      organizationId: org?.id || 0,
      progressivePlanId: selectedPlan.id,
      userId: user?.id ? Number(user.id) : 0,
      amount: amountStr,
      currency: "USD",
    };
  };

  const getQuotaIcon = (type: ProgressivePricingType) => {
    switch (type) {
      case "USERS":
        return Users;
      case "STORIES":
        return FileText;
      default:
        return Battery;
    }
  };

  const getQuotaColor = (type: ProgressivePricingType) => {
    switch (type) {
      case "USERS":
        return "blue";
      case "STORIES":
        return "green";
      default:
        return "gray";
    }
  };

  const getUsagePercentage = (balance: number, quota: number) => {
    if (quota === 0) return 0;
    return ((quota - balance) / quota) * 100;
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return "red";
    if (percentage >= 70) return "yellow";
    return "green";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading quotas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quotas & Balance</h2>
          <p className="text-gray-600">
            Manage your organization's usage quotas and top up when needed
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-600">Organization</div>
          <div className="font-semibold text-gray-900">
            {currentOrganization?.name || "Loading..."}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {topupSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">{topupSuccess}</p>
            </div>
          </div>
        </div>
      )}

      {/* Quotas Grid */}
      {quotas.length === 0 ? (
        <div className="text-center py-12">
          <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Quotas Found
          </h3>
          <p className="text-gray-600">
            No progressive plan quotas are configured for your organization.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quotas.map((quota) => {
            const IconComponent = getQuotaIcon(quota.progressivePricingType);
            const color = getQuotaColor(quota.progressivePricingType);
            const usagePercentage = getUsagePercentage(
              quota.balance,
              quota.quota
            );
            const usageColor = getUsageColor(usagePercentage);
            const used = quota.quota - quota.balance;

            return (
              <div
                key={quota.id}
                className={`bg-white rounded-lg shadow-lg p-6 border-l-4 border-${color}-500`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-3 bg-${color}-100 rounded-lg`}>
                      <IconComponent className={`h-6 w-6 text-${color}-600`} />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {quota.progressivePricingType === "USERS"
                          ? "Users"
                          : "Stories"}
                      </h3>
                      <p className="text-sm text-gray-600">Quota Management</p>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Quota:</span>
                    <span className="font-semibold text-gray-900">
                      {quota.quota}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Used:</span>
                    <span className="font-semibold text-gray-900">{used}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Balance:</span>
                    <span
                      className={`font-semibold ${
                        quota.balance > 5 ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {quota.balance}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Usage</span>
                      <span>{usagePercentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          usageColor === "red"
                            ? "bg-red-500"
                            : usageColor === "yellow"
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                        style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Warning if low balance */}
                  {quota.balance <= 5 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-4">
                      <div className="flex">
                        <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
                        <div className="ml-2">
                          <p className="text-xs text-red-800">
                            Low balance warning! Consider topping up soon.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Up Button */}
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(
                          "Top Up button clicked for quota ID:",
                          quota.id
                        );
                        setShowTopupModal(quota.id);
                      }}
                      className={`w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-${color}-600 hover:text-${color}-800 hover:bg-${color}-50 rounded-md transition-colors border border-${color}-300 hover:border-${color}-400`}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Top Up Credits
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Topup Modal */}
      {showTopupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Top Up Quota
              </h3>
              <button
                onClick={() => setShowTopupModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {(() => {
                const currentQuota = quotas.find(
                  (q) => q.id === showTopupModal
                );
                const relevantPlans = availableTopupPlans.filter(
                  (plan) => plan.type === currentQuota?.progressivePricingType
                );

                return (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Top-Up Plan
                      </label>
                      <select
                        value={selectedTopupPlan[showTopupModal] || ""}
                        onChange={(e) =>
                          setSelectedTopupPlan({
                            ...selectedTopupPlan,
                            [showTopupModal]: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select a plan...</option>
                        {relevantPlans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            ${(plan.priceCents / 100).toFixed(0)} -{" "}
                            {plan.planLimit} {plan.type.toLowerCase()}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedTopupPlan[showTopupModal] &&
                      (() => {
                        const selectedPlan = relevantPlans.find(
                          (p) => p.id === selectedTopupPlan[showTopupModal]
                        );
                        return selectedPlan ? (
                          <>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                              <p className="text-sm text-blue-800">
                                💡 This will add {selectedPlan.planLimit}{" "}
                                credits to your quota balance for $
                                {(selectedPlan.priceCents / 100).toFixed(0)}{" "}
                                USD.
                              </p>
                            </div>
                            {/* Removed development-only PayPal notice */}
                          </>
                        ) : null;
                      })()}
                  </>
                );
              })()}

              {toppingUp === showTopupModal ? (
                <div className="flex items-center justify-center py-4">
                  <Loader className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                  <span className="text-gray-600">Processing payment...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowTopupModal(null)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 mr-3"
                    >
                      Cancel
                    </button>
                  </div>

                  {selectedTopupPlan[showTopupModal] ? (
                    paypalClientId ? (
                      <div className="paypal-container">
                        <PayPalButtons
                          style={{
                            layout: "vertical",
                            color: "gold",
                            shape: "rect",
                            label: "paypal",
                          }}
                          onInit={() =>
                            console.log("PayPalButtons initialized")
                          }
                          onClick={() => console.log("PayPalButtons clicked")}
                          createOrder={async () => {
                            try {
                              const payload =
                                buildCreateOrderPayload(showTopupModal);
                              console.log(
                                "Creating PayPal order via backend:",
                                payload
                              );
                              const resp = await realAPI.createPayPalOrder(
                                payload
                              );
                              console.log(
                                "Backend create order response:",
                                resp
                              );
                              if (!resp?.orderID)
                                throw new Error(
                                  "No orderID returned by backend"
                                );
                              return resp.orderID;
                            } catch (e: any) {
                              handlePayPalError(e);
                              // Re-throw to let PayPal UI handle failure state
                              throw e;
                            }
                          }}
                          onApprove={async (data) => {
                            try {
                              console.log("PayPal payment approved:", data);
                              const selectedPlanId =
                                selectedTopupPlan[showTopupModal];
                              const selectedPlan = availableTopupPlans.find(
                                (p) => p.id === selectedPlanId
                              );
                              if (!selectedPlan)
                                throw new Error("No plan selected");

                              const expectedAmount = (
                                selectedPlan.priceCents / 100
                              ).toFixed(2);
                              const capturePayload = {
                                expectedAmount,
                                expectedCurrency: "USD",
                                progressivePlanId: selectedPlan.id,
                                creditAmount: selectedPlan.planLimit,
                              } as const;

                              setToppingUp(showTopupModal);
                              const capture = await realAPI.capturePayPalOrder(
                                String(data.orderID),
                                capturePayload
                              );
                              console.log("Backend capture response:", capture);
                              if (!capture.ok) {
                                throw new Error(
                                  capture.error ||
                                    `Capture failed with status ${capture.status}`
                                );
                              }

                              // Success UI updates
                              setTopupSuccess(
                                `✅ Payment processed! Added ${selectedPlan.planLimit} credits. Capture ID: ${capture.captureId}`
                              );
                              setShowTopupModal(null);
                              setSelectedTopupPlan({
                                ...selectedTopupPlan,
                                [showTopupModal]: 0,
                              });
                              await loadData();
                              if (onUpdate) onUpdate();
                            } catch (err: any) {
                              console.error(
                                "PayPal approve/capture error:",
                                err
                              );
                              handlePayPalError(err);
                            } finally {
                              setToppingUp(null);
                            }
                          }}
                          onError={(err) => {
                            console.error("PayPal error:", err);
                            handlePayPalError(err);
                          }}
                          onCancel={() => {
                            console.log("PayPal payment cancelled");
                          }}
                        />
                      </div>
                    ) : null
                  ) : (
                    <div className="text-center py-4">
                      <CreditCard className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">
                        Select a plan above to proceed with payment
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotaManagement;
